import React from "react";
import { motion } from "framer-motion";
import { Beaker, Award, Users, Leaf, ShieldCheck, BadgeCheck, Mail, Phone } from "lucide-react";
import SEO from "../components/SEO";

export default function About() {
  return (
    <div className="pt-32 pb-24" data-testid="about-page">
      <SEO
        path="/sobre"
        title="Sobre · Quem somos"
        description="Biofarmax é uma farmácia de manipulação em Campinas/SP que une tradição farmacêutica e inovação científica. Fórmulas precisas, naturais e personalizadas para cada paciente."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "Sobre a Biofarmax",
          "url": "https://biofarmax.com.br/sobre",
          "mainEntity": { "@id": "https://biofarmax.com.br/#organization" },
        }}
      />
      <div className="max-w-5xl mx-auto px-5 md:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="eyebrow mb-4">Quem somos</div>
          <h1 className="h-display text-6xl md:text-7xl mb-8">
            Cuidar de você é uma <span className="italic text-brand-accent">arte</span> e uma ciência.
          </h1>
          <p className="text-lg text-ink-secondary leading-relaxed max-w-3xl">
            A Biofarmax é uma farmácia de manipulação dedicada a entregar fórmulas precisas, naturais e personalizadas para cada paciente. Localizada na Região Metropolitana de Campinas, unimos tradição farmacêutica com inovação científica.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-20">
          {[
            { Icon: Beaker, t: "Laboratório próprio", d: "Manipulação interna com matérias-primas selecionadas." },
            { Icon: Award, t: "Excelência farmacêutica", d: "Equipe técnica habilitada e processos certificados." },
            { Icon: Users, t: "Atendimento humano", d: "Acompanhamento dedicado em cada formulação." },
            { Icon: Leaf, t: "Fitoterapia premium", d: "Mais de 70 fórmulas com extratos naturais." },
          ].map((v, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="p-6 bg-white rounded-3xl border border-black/5"
            >
              <v.Icon size={28} className="text-brand-accent mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-xl mb-2">{v.t}</h3>
              <p className="text-sm text-ink-secondary leading-relaxed">{v.d}</p>
            </motion.div>
          ))}
        </div>

        {/* Farmacêutico Responsável */}
        <motion.section
          id="farmaceutico-responsavel"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-24 scroll-mt-32"
          data-testid="pharmacist-section"
        >
          <div className="eyebrow mb-4">Responsabilidade técnica</div>
          <h2 className="h-display text-4xl md:text-5xl mb-8">
            Farmacêutico <span className="italic text-brand-accent">responsável</span>
          </h2>

          <div className="bg-gradient-to-br from-white to-bg-secondary rounded-3xl border border-brand-accent/20 p-8 md:p-10 shadow-[0_30px_80px_-40px_rgba(201,180,122,0.35)]">
            <div className="flex items-start gap-5 mb-6">
              <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-brand-accent grid place-items-center">
                <ShieldCheck size={32} className="text-white" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest font-tech text-ink-secondary mb-1">Responsável técnico</div>
                <h3 className="font-heading text-3xl md:text-4xl text-brand-primary mb-2" data-testid="pharmacist-name">
                  Reinaldo Stefani Neto
                </h3>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-accent/10 text-brand-accent text-sm font-tech">
                  <BadgeCheck size={14} strokeWidth={2} /> CRF-SP: 21860
                </div>
              </div>
            </div>

            <p className="text-ink-secondary leading-relaxed mb-6">
              Todo o processo de manipulação, controle de qualidade e liberação de fórmulas na Biofarmax é supervisionado diretamente pelo nosso farmacêutico responsável, garantindo a conformidade com as normas da ANVISA, RDC 67/2007 e as boas práticas de manipulação farmacêutica.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 pt-6 border-t border-black/5">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-tech text-ink-secondary mb-1">Razão Social</div>
                <div className="text-sm font-medium">Biofarmax Farmácia de Manipulação</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-tech text-ink-secondary mb-1">CNPJ</div>
                <div className="text-sm font-tech" data-testid="company-cnpj">01.853.857/0001-17</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-tech text-ink-secondary mb-1">Autorização</div>
                <div className="text-sm">CRF-SP · ANVISA</div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-black/5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-secondary">
              <a href="mailto:contato@biofarmax.com.br" className="inline-flex items-center gap-2 hover:text-brand-accent transition">
                <Mail size={14} /> contato@biofarmax.com.br
              </a>
              <a href="tel:+551992923017" className="inline-flex items-center gap-2 hover:text-brand-accent transition">
                <Phone size={14} /> (19) 99292-3017
              </a>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
